<template>
  <span ref="user_avatar" class="tw:flex">
    <v-skeleton-loader
      v-if="isLoading"
      :height="size"
      :width="size"
      class="tw:[&_.v-skeleton-loader\_\_avatar]:size-full"
      type="avatar"
    />
    <template v-else>
      <v-avatar
        :class="{
          'tw:has-[.avatar-initials]:bg-primary': selectedUser,
          'tw:bg-gray-200': !selectedUser,
        }"
        v-bind="{ size, ...settings }"
      >
        <img v-if="avatarState === 'loaded' && avatar" :src="avatar" />
        <template v-else-if="avatarState === 'none' || hasCustomAvatar">
          <span v-if="selectedUser" class="avatar-initials tw:text-white tw:font-medium">{{
            selectedUser?.name.charAt(0)?.toUpperCase()
          }}</span>
          <v-icon v-else class="tw:text-gray-500" :size="size / 1.5">mdi-account</v-icon>
        </template>
      </v-avatar>
    </template>
    <v-tooltip v-if="showTooltip" :activator="$refs.user_avatar" bottom color="font">
      {{ selectedUser?.name ?? fallbackTooltip }}
    </v-tooltip>
  </span>
</template>

<script>
import { get } from 'vuex-pathify';

export default {
  name: 'UserAvatar',
  props: {
    id: {
      type: [Number, String] /** User ID or 'current' flag for current user */,
      default: 'current',
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
    showTooltip: {
      type: Boolean,
      default: false,
    },
    fallbackTooltip: {
      type: String,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    customAvatar: {
      type: String,
    },
  },
  data: () => ({
    avatarState: 'loading', // loading | loaded | none
    revision: 0,
  }),
  computed: {
    token: get('authentication/token'),
    users: get('users/users'),
    currentUser: get('users/currentUser'),
    selectedUser() {
      return this.id === 'current' ? this.currentUser : this.users[this.id];
    },
    isCurrentUserAvatar() {
      return this.currentUser?.id === this.selectedUser?.id;
    },
    isLoading() {
      return this.loading || this.avatarState === 'loading';
    },
    hasCustomAvatar() {
      return this.customAvatar !== undefined;
    },
    staticPhotoUrl() {
      return `${import.meta.env.VUE_APP_API_URL}/users/avatar/${this.selectedUser?.id}?v=${this.revision}&token=${this.token}`;
    },
    avatar() {
      return this.hasCustomAvatar ? this.customAvatar : this.staticPhotoUrl;
    },
    size() {
      return this.settings.size ?? 35;
    },
    fontSize() {
      return `${this.size * 0.5}px`;
    },
  },
  watch: {
    staticPhotoUrl: {
      handler() {
        this.preloadImage();
      },
      immediate: true,
    },
    hasCustomAvatar: {
      handler(nV) {
        if (nV) this.avatarState = 'loaded';
      },
      immediate: true,
    },
  },
  methods: {
    preloadImage() {
      if (this.hasCustomAvatar) return (this.avatarState = 'loaded');
      if (!this.selectedUser?.id) return (this.avatarState = 'none');
      const img = new Image();
      img.src = this.staticPhotoUrl;
      img.onload = () => {
        this.$emit('onAvatar', this.staticPhotoUrl);
        this.avatarState = 'loaded';
      };
      img.onerror = () => {
        this.$emit('onAvatar', null);
        this.avatarState = 'none';
      };
    },
  },
  updated() {
    this.$emit('update');
  },
  created() {
    /** Increasing avatar's version to reload cache */
    if (!this.isCurrentUserAvatar) return;
    /** Client-side version revision to control cache after CRUD actions */
    this.revision = parseInt(localStorage.getItem('avatar_revision') ?? '0');
    this.$root.$on('onAvatarChanged', () => {
      this.revision += 1;
      localStorage.setItem('avatar_revision', this.revision);
    });
  },
};
</script>

<style scoped>
.avatar-initials {
  font-size: v-bind(fontSize);
}
</style>
